import type { GetEstimateInfoMinimalByIdResponse } from '@massilia/queries'

import { useAppForm } from '@massilia/features'
import { useCreateReservationMutation } from '@massilia/queries'
import { revalidateLogic, standardSchemaValidators } from '@tanstack/react-form'
import { useNavigate } from '@tanstack/react-router'
import { differenceInDays, parseISO } from 'date-fns'

import { ImportantNotice } from '@/components/ui/important-notice'
import {
  type ReservationPassengerSchema,
  reservationSchema,
} from '@/lib/schemas/reservation/reservation.schema'

import { ContactDetailsFormGroup } from '../-form-groups/contact-details-form-group'
import { FinalOptionsFormGroup } from '../-form-groups/final-options-form-group'
import { PassengersFormGroup } from '../-form-groups/passengers-form-group'

interface ReservationFormProps {
  estimateResponseId: string
  estimateData: GetEstimateInfoMinimalByIdResponse
  withInsurance: boolean
}

export function ReservationForm({
  estimateResponseId,
  estimateData,
  withInsurance,
}: ReservationFormProps) {
  const createReservationMutation = useCreateReservationMutation()
  const navigate = useNavigate()

  // Prepare initial passengers from estimate data
  const initialPassengers: ReservationPassengerSchema[] = estimateData.passengers.map((p) => ({
    firstName: p.firstName,
    lastName: p.lastName,
    passengerId: p.id,
    birthDate: p.birthDate,
    fidelityCards:
      p.fidelityCards?.map((card) => ({
        id: card.id,
        cardNumber: card.cardNumber,
        company: { label: card.companyId, value: card.companyId },
      })) ?? [],
    gender: p.gender,
    identityDocuments:
      p.identityDocuments?.map((doc) => ({
        ...doc,
        issuingCountry: { label: doc.issuingCountryLabel, value: doc.issuingCountry },
      })) ?? [],
    nationality: p.nationalityCode,
  }))

  const form = useAppForm({
    defaultValues: {
      acceptsTermsAndConditions: false,
      animalInformation: '',
      complementaryInformation: '',
      newsletterSubscription: 'NO' as 'ALREADY_SUBSCRIBED' | 'NO' | 'YES',
      otherContactPhone: '',
      otherEmailAddress: '',
      passengers: initialPassengers,
      seatInformation: '',
      wantsCarLoan: false,
      wantsInsurance: withInsurance,
      wantsInvoice: false,
    },
    onSubmit: async ({ value }) => {
      try {
        const reservation = await createReservationMutation.mutateAsync({
          flightEstimateResponseId: estimateData.type === 'flight' ? estimateResponseId : undefined,
          tourismEstimateResponseId:
            estimateData.type === 'tourism' ? estimateResponseId : undefined,
          animalInformation: value.animalInformation,
          complementaryInformation: value.complementaryInformation,
          newsletterSubscription: value.newsletterSubscription,
          otherContactPhone: value.otherContactPhone ?? '',
          otherEmailAddress: value.otherEmailAddress ?? '',
          passengers: value.passengers.map((p) => ({
            ...p,
            fidelityCards: p.fidelityCards
              ?.filter((card) => card.cardNumber && card.company?.value)
              .map((card) => ({
                companyId: card.company.value,
                id: card.id,
                cardNumber: card.cardNumber,
              })),
            identityDocuments: p.identityDocuments
              ?.filter((doc) => doc.documentNumber)
              .map((doc) => ({
                ...doc,
                issuingCountry: doc.issuingCountry.value,
              })),
            nationality: p.nationality,
          })),
          seatInformation: value.seatInformation,
          wantsCarLoan: value.wantsCarLoan,
          wantsInsurance: value.wantsInsurance,
          wantsInvoice: value.wantsInvoice,
        })
        void navigate({
          params: { reservationId: reservation.id },
          to: '/confirmation-reservation/$reservationId',
        })
      } catch {
        // Error handling is done by the mutation
      }
    },
    validationLogic: revalidateLogic({
      mode: 'submit',
      modeAfterSubmission: 'change',
    }),
    validators: {
      onDynamic: ({ value }) => {
        return standardSchemaValidators.validate(
          { validationSource: 'form', value },
          reservationSchema
        )
      },
    },
  })

  return (
    <div className="flex w-full flex-col gap-9">
      <div className="text-gray-light-900 text-2xl font-semibold">Réservation suite à un devis</div>

      {/* Important notices */}
      <ImportantNotice variant="warning" title="Vérification importante">
        <ul className="list-inside list-disc space-y-1 text-xs">
          <li>
            Vérifiez que les noms / prénoms correspondent exactement aux documents d&apos;identité
            (passeport / carte identité)
          </li>
          <li>Pour les femmes mariées : indiquer le nom de jeune fille suivi du nom marital</li>
          <li>Vérifiez que l&apos;adresse e-mail et le numéro de téléphone sont corrects</li>
          <li>Assurez-vous d&apos;être en règle avec les formalités de police, douane et santé</li>
        </ul>
      </ImportantNotice>

      {/* Form */}
      <form
        onSubmit={(e) => {
          e.preventDefault()
          e.stopPropagation()
          void form.handleSubmit()
        }}
        className="space-y-8"
      >
        {/* Section 1: Contact details (with client info) */}
        <ContactDetailsFormGroup
          form={form}
          fields={{
            complementaryInformation: 'complementaryInformation',
            otherContactPhone: 'otherContactPhone',
            otherEmailAddress: 'otherEmailAddress',
          }}
          client={estimateData.client}
        />

        {/* Section 2: Passengers */}
        <PassengersFormGroup form={form} fields={{ passengers: 'passengers' }} />

        {/* Section 3: Final options and submit */}
        <form.Subscribe selector={(state) => state.isSubmitting}>
          {(isSubmitting) => (
            <FinalOptionsFormGroup
              form={form}
              fields={{
                acceptsTermsAndConditions: 'acceptsTermsAndConditions',
                animalInformation: 'animalInformation',
                newsletterSubscription: 'newsletterSubscription',
                seatInformation: 'seatInformation',
                wantsCarLoan: 'wantsCarLoan',
                wantsInsurance: 'wantsInsurance',
                wantsInvoice: 'wantsInvoice',
              }}
              companyIataCode={estimateData.companyIataCode}
              estimateDate={new Date(estimateData.estimateDate)}
              estimateReference={estimateResponseId}
              estimateType={estimateData.type}
              insuranceOption={estimateData.insuranceOption}
              isLongTrip={getIsLongTrip(estimateData.flightSegments)}
              isSubmitting={isSubmitting}
              totalPrice={estimateData.totalPrice}
              totalPriceWithInsurance={estimateData.totalPriceWithInsurance}
            />
          )}
        </form.Subscribe>
      </form>
    </div>
  )
}

/**
 * Determine if trip is longer than 89 days (3 months) based on flight segments.
 * Uses the first and last segment departure dates.
 */
function getIsLongTrip(flightSegments: { arrivalAt: string; departureAt: string }[]): boolean {
  if (!flightSegments || flightSegments.length < 2) return false

  const sorted = [...flightSegments].sort(
    (a, b) => parseISO(a.departureAt).getTime() - parseISO(b.departureAt).getTime()
  )

  const firstDeparture = parseISO(sorted[0].departureAt)
  const lastDeparture = parseISO(sorted[sorted.length - 1].departureAt)

  return differenceInDays(lastDeparture, firstDeparture) > 89
}
